import ApiService from '@/common/api.service'
import vehicleAttachmentsModule from './vehicleAttachmentsModel'
import vehicleNotesModule from './vehicleNotesModule'

import * as utils from '@/common/utils'
import * as attachmentsHelpers from '@/store/attachments/attachments.helpers'
import * as notesHelpers from '@/store/notes/notes.helpers'
import * as security from '@/common/security'
import store from '@/store'

const module = 'assets/vehiclesModule'
const attachmentsModule = module + '/attachments'
const notesModule = module + '/notes'

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    vehicle_event_profile_id: '',
    reg_no: '',
    fleet_no: '',
    fin_account_no: '',
    fin_sub_account_no: '',
    icon_name: null,
    vehicle_status_code: 'active',
    vehicle_status_text: 'Active',
    vin: '',
    engine_no: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_color: '',
    vehicle_year: '',
    odo_veh_km: '',
    odo_gps_km: '',
    service_interval_km: '',
    service_interval_month: '',
    prev_service_km: '',
    next_service_km: '',
    prev_license_date: '',
    license_exp_date: '',
    prev_service_date: '',
    next_service_date: '',
    prev_permit_date: '',
    permit_exp_date: '',
    partner: {
      id: null,
      name: ''
    },
    agency: {
      id: null,
      name: ''
    },
    client: {
      id: null,
      name: ''
    },
    driver: {
      id: null,
      name: ''
    },
    vehicle_group: {
      id: null,
      name: ''
    },
    assigned_units: [],
    created_at: '',
    updated_at: '',
    note: '',
    telemetry: null,

    addingUnit: false,
    assignUnitAs: null,
    sensorsSelectedUnitId: null,
    sensorsEditingId: null
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
    partners: [],
    drivers: [],
    vehicleGroups: [],
    vehicle_statuses: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param vehicle      Object
 * @param attachments Array|null
 * @param notes       Array|null
 *
 * @return Object
 */
function transformCreate (vehicle, attachments = null, notes = null) {
  return {
    partner_id: vehicle.partner.id,
    agency_id: vehicle.agency.id,
    client_id: vehicle.client.id,
    id: vehicle.id,
    driver_id: vehicle.driver.id,
    vehicle_event_profile_id: vehicle.vehicle_event_profile_id,
    vehicle_group_id: vehicle.vehicle_group.id === null ? null : parseInt(vehicle.vehicle_group.id),
    reg_no: vehicle.reg_no,
    fleet_no: vehicle.fleet_no,
    fin_account_no: vehicle.fin_account_no,
    fin_sub_account_no: vehicle.fin_sub_account_no,
    vehicle_status_code: vehicle.vehicle_status_code,
    icon_name: vehicle.icon_name === '' ? null : vehicle.icon_name,
    vin: vehicle.vin,
    engine_no: vehicle.engine_no,
    vehicle_make: vehicle.vehicle_make,
    vehicle_model: vehicle.vehicle_model,
    vehicle_color: vehicle.vehicle_color,
    vehicle_year: vehicle.vehicle_year,
    odo_veh_km: vehicle.odo_veh_km,
    odo_gps_km: vehicle.odo_gps_km,
    service_interval_km: vehicle.service_interval_km,
    service_interval_month: vehicle.service_interval_month,
    prev_service_km: vehicle.prev_service_km,
    next_service_km: vehicle.next_service_km,
    prev_license_date: vehicle.prev_license_date,
    license_exp_date: vehicle.license_exp_date,
    prev_service_date: vehicle.prev_service_date,
    next_service_date: vehicle.next_service_date,
    prev_permit_date: vehicle.prev_permit_date,
    permit_exp_date: vehicle.permit_exp_date,
    attachments,
    notes,
    units: vehicle.assigned_units.map((unit) => {
      return {
        id: unit.id,
        sensors: unit.sensors,
        assigned_as: unit.assigned_as,
        client_event_profile_id: unit.client_event_profile_id
      }
    })
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param name String
 *
 * @return Object
 * @param partner_id
 * @param agency_id
 * @param client_id
 */
function transformCreateGroup (name, partner_id, agency_id, client_id) {
  return {
    id: null,
    name: name,
    partner_id: partner_id,
    agency_id: agency_id,
    client_id: client_id
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param vehicle  Object
 *
 * @return Object
 */
function transformUpdate (vehicle) {
  return {
    partner_id: vehicle.partner.id,
    agency_id: vehicle.agency.id,
    client_id: vehicle.client.id,
    id: vehicle.id,
    driver_id: vehicle.driver.id,
    vehicle_event_profile_id: vehicle.vehicle_event_profile_id,
    vehicle_group_id: vehicle.vehicle_group.id === null ? null : parseInt(vehicle.vehicle_group.id),
    reg_no: vehicle.reg_no,
    fleet_no: vehicle.fleet_no,
    fin_account_no: vehicle.fin_account_no,
    fin_sub_account_no: vehicle.fin_sub_account_no,
    vehicle_status_code: vehicle.vehicle_status_code,
    icon_name: vehicle.icon_name === '' ? null : vehicle.icon_name,
    vin: vehicle.vin,
    engine_no: vehicle.engine_no,
    vehicle_make: vehicle.vehicle_make,
    vehicle_model: vehicle.vehicle_model,
    vehicle_color: vehicle.vehicle_color,
    vehicle_year: vehicle.vehicle_year,
    odo_veh_km: vehicle.odo_veh_km,
    odo_gps_km: vehicle.odo_gps_km,
    service_interval_km: vehicle.service_interval_km,
    service_interval_month: vehicle.service_interval_month,
    prev_service_km: vehicle.prev_service_km,
    next_service_km: vehicle.next_service_km,
    prev_license_date: vehicle.prev_license_date,
    license_exp_date: vehicle.license_exp_date,
    prev_service_date: vehicle.prev_service_date,
    next_service_date: vehicle.next_service_date,
    prev_permit_date: vehicle.prev_permit_date,
    permit_exp_date: vehicle.permit_exp_date,
    units: vehicle.assigned_units.map((unit) => {
      return {
        id: unit.id,
        sensors: unit.sensors,
        assigned_as: unit.assigned_as,
        client_event_profile_id: unit.client_event_profile_id
      }
    })
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`vehicles/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Fetches drivers linked to client selected in org
   *
   * @param context
   * @param id
   */
  fetchDrivers (context, id) {
    ApiService
      .get(`drivers/listing/by-client/${id}`)
      .then((response) => {
        context.commit(`setDrivers`, response.data.data)
      })
  },

  /**
   * Fetches vehicle groups linked to client selected in org
   *
   * @param context
   * @param id
   */
  fetchVehicleGroups (context, id) {
    let vehicle = context.getters['get']
    ApiService
      .get(`vehicles/groups/listing/by-client/${id}`)
      .then((response) => {
        context.commit(`setVehicleGroups`, response.data.data)

        let is_select_tag = isNaN(utils.asNumberOrNull(vehicle.vehicle_group.id))
        if (is_select_tag) {
          context.commit('setVehicleGroupId', null)
        }
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`vehicles/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        attachmentsHelpers.purge(attachmentsModule)
        notesHelpers.purge(notesModule)
        context.commit('setIsModified', false)

        if (!security.belongsToMultipleClients() && security.isClient()) {
          let client_id = security.user().clients[0].id
          ApiService
            .get(`drivers/listing/by-client/${client_id}`)
            .then((response) => {
              if (response.data.data.length > 0) {
                context.commit(`setDrivers`, response.data.data)
              }
            })
        }

        if (!security.belongsToMultipleClients() && security.isClient()) {
          let client_id = security.user().clients[0].id
          ApiService
            .get(`vehicles/groups/listing/by-client/${client_id}`)
            .then((response) => {
              if (response.data.data.length > 0) {
                context.commit(`setVehicleGroups`, response.data.data)
              }
            })
        }

        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`vehicles/${id}/edit/?includes=telemetry`)
      .then((response) => {
        const vehicle = response.data.data

        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        attachmentsHelpers.setAttachments(attachmentsModule, response.data.data.attachments)
        notesHelpers.setNotes(notesModule, response.data.data.notes)
        context.commit('setIsModified', false)

        if (response.data.data.driver === null) {
          context.commit('setDriverDefaults')
        }

        if (response.data.data.vehicle_group === null) {
          context.commit('setGroupDefaults')
        }

        if (response.data.data.assigned_units.length > 0) {
          context.commit('sortUnitsByAssignment')
          context.commit('setSensorsSelectedUnitId', response.data.data.assigned_units[0].id)
        }

        ApiService
          .get(`drivers/listing/by-client/${vehicle.client.id}`)
          .then((response) => {
            if (response.data.data.length > 0) {
              context.commit(`setDrivers`, response.data.data)
            }
          })

        ApiService
          .get(`vehicles/groups/listing/by-client/${vehicle.client.id}`)
          .then((response) => {
            if (response.data.data.length > 0) {
              context.commit(`setVehicleGroups`, response.data.data)
            }
          })
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let notes = []
    let note = context.getters['get'].note || null
    if (note !== null && note.trim() !== '') {
      notes.push(note)
    }

    let attachments = attachmentsHelpers.attachments(attachmentsModule)

    return ApiService
      .post(`vehicles`, transformCreate(context.getters['get'], attachments, notes))
      .then((response) => {
        attachmentsHelpers.purge(attachmentsModule)
        notesHelpers.purge(notesModule)
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * POST (create) new vehicle group record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @param data
   * @return Promise
   */
  postVehicleGroup (context, data) {
    return ApiService
      .post(`vehicles/groups`, transformCreateGroup(
        data.data.name, data.data.partner_id, data.data.agency_id, data.data.client_id))
      .then((response) => {
        ApiService
          .get(`vehicles/groups/listing/by-client/${response.data.data.client.id}`)
          .then((group_response) => {
            context.commit(`setVehicleGroups`, group_response.data.data)
            context.commit('setVehicleGroupId', response.data.data.id)
          })
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const vehicle = context.getters['get']
    return ApiService
      .put(`vehicles/${vehicle.id}`, transformUpdate(vehicle))
      .then((response) => {
        // If new units was assigned to the vehicle then update the
        // assigned unit's vehicle_id and assigned_as fields so we
        // don't need to re-load the units
        context.commit('applyAssignedUnitState')

        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`vehicles/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryHistory (context, params) {
    const id = context.getters['get'].id

    return ApiService
      .get(`vehicles/${id}/history`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`vehicles`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   * @param data   Object
   * @return Promise
   */
  putAssignedDriver (context, data) {
    return ApiService
      .put(`vehicles/${data.data.vehicle_id}/assign-driver`, { driver_id: data.data.driver_id })
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners,
      drivers: [],
      vehicleGroups: [],
      vehicle_statuses: data.vehicle_statuses
    }
  },

  setVehicleGroupId (state, id) {
    state.data.vehicle_group.id = id
  },

  setSelectedUnitId (state, id) {
    state.data.sensorsSelectedUnitId = id
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = Object.assign(defaults(), data)
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    utils.pullProp(state.data, name, index)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * Sets the drivers in form.
   *
   * @param state   Object
   * @param drivers Array of objects in the form { id: 1, name: 'xyz' }
   */
  setDrivers (state, drivers) {
    state.form.drivers = drivers
  },

  /**
   * Sets the assigned_units in state data.
   *
   * @param state
   * @param units
   */
  setUnits (state, units) {
    state.data.assigned_units = units
  },

  /**
   * Sets the vehicle groups in form.
   *
   * @param state   Object
   * @param groups Array of objects in the form { id: 1, name: 'xyz' }
   */
  setVehicleGroups (state, groups) {
    state.form.vehicleGroups = groups
  },

  /**
   * Transforms the driver state from null to object.
   * This is done because if no driver was assigned to
   * the vehicle the driver is set to null if
   * formFetchEdit is called instead of {id: null, name: ''}.
   * This causes an error when user tries to assign a driver
   * in the edit screens.
   *
   * @param state   Object
   */
  setDriverDefaults (state) {
    state.data.driver = {
      id: null,
      name: ''
    }
  },

  /**
   * Transforms the vehicle_group state from null to object.
   * This is done because if no vehicle group was assigned to
   * the vehicle the vehicle group is set to null if
   * formFetchEdit is called instead of {id: null, name: ''}.
   * This causes an error when user tries to assign a vehicle group
   * in the edit screens.
   *
   * @param state   Object
   */
  setGroupDefaults (state) {
    state.data.vehicle_group = {
      id: null,
      name: ''
    }
  },

  /**
   * Sorts the units so that it is listed in the order:
   * primary -> backup_1 -> backup_2
   *
   * @param state
   */
  sortUnitsByAssignment (state) {
    let sorted_units = []
    const primaryUnit = state.data.assigned_units.find(unit => unit.assigned_as === 'primary')
    const backup01Unit = state.data.assigned_units.find(unit => unit.assigned_as === 'backup_1')
    const backup02Unit = state.data.assigned_units.find(unit => unit.assigned_as === 'backup_2')
    if (primaryUnit) sorted_units.push(primaryUnit)
    if (backup01Unit) sorted_units.push(backup01Unit)
    if (backup02Unit) sorted_units.push(backup02Unit)

    state.data.assigned_units = sorted_units
  },

  /**
   * Apply the state of newly assigned units.
   *
   * @param state
   */
  applyAssignedUnitState (state) {
    state.data.assigned_units.forEach(unit => {
      if (unit.vehicle_id === null) {
        unit.vehicle_id = state.data.id
      }
    })
  },

  /**
   * Update a sensor on a unit that have already been assigned to the vehicle.
   *
   * @param state
   *
   * @param unitId
   * @param sensor
   */
  updateAssignedSensor (state, {unitId, sensor}) {
    let assignedUnit = state.data.assigned_units.find(tmpUnit => tmpUnit.id === unitId)
    if (assignedUnit) {
      const sensorIdx = assignedUnit.sensors.findIndex(tmpSensor => tmpSensor.id === sensor.id)
      assignedUnit.sensors.splice(sensorIdx, 1, sensor)
    }
  },

  /**
   * Sets the selected unit id for sensors being edited.
   *
   * @param state
   * @param unitId
   */
  setSensorsSelectedUnitId (state, unitId) {
    state.data.sensorsSelectedUnitId = unitId
  },

  /**
   * Set the vehicle's partner, agency and client id's.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    store.commit('assets/vehiclesModule/clearClient')
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner.id
      state.data.partner.name = client.partner.name
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = client.agency.id
      state.data.agency.name = client.agency.name
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
    }
  },

  /**
   * Clear the vehicle's partner agency and client data.
   *
   * @param state
   */
  clearClient (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules: {
    attachments: vehicleAttachmentsModule,
    notes: vehicleNotesModule
  }
}



// WEBPACK FOOTER //
// ./src/store/assets/vehicles/vehiclesModule.js